import { SearchEmptyState } from "@louise/moon-ui/primitives/SearchEmptyState";
import { EmptyState } from "@louise/moon-ui/primitives/EmptyState";
import { DialogIcon } from "@louise/moon-ui/primitives/DialogIcon";
import { CompactActionButton } from "@louise/moon-ui/primitives/CompactActionButton";
import documentsIllustration from "../../assets/images/document-review-icons/source-documents-3d.png";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Platform,
  Animated,
  Easing,
} from "react-native";
import ReAnimated, { FadeIn, LinearTransition } from "react-native-reanimated";
import {
  ListRowSkeleton,
  MoonFilterChip,
  ClinicalDataStatus,
  MoonSearchInput,
  BackLink,
  SectionTitle,
  cardSurface,
} from "@louise/moon-ui";
import {
  Plus,
  FileText,
  Calendar,
  AlertCircle,
  ClipboardList,
  File,
  Upload,
  ShieldCheck,
  Layers,
  type LucideIcon,
} from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { EMBER } from "../../onboarding/theme";
import {
  useDocuments,
  type Document,
} from "@src/packages/documents/hooks/useDocuments";
import { useTestResults } from "@src/packages/test-results/hooks/useTestResults";
import type { TestResult } from "@src/packages/test-results/hooks/useTestResults";
import type {
  TreatmentIntent,
  PartnerChoice,
  Condition,
} from "../../onboarding/types";
import type { CareStackParamList } from "../types";
import { useSmartBack } from "../hooks/useSmartBack";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import {
  useTranslations,
  useLocale,
  type TranslationFunction,
} from "@src/packages/i18n";
import DocumentUploadModal, {
  DocumentFileDropZone,
  type DocumentSlotInfo,
  type SelectedFile,
} from "@src/packages/document-vault/DocumentUploadModal";
import DocumentUploadInteractionPrototype, {
  DOCUMENT_UPLOAD_PROTOTYPE_STATES,
  type DocumentUploadPrototypeState,
} from "@src/packages/document-vault/DocumentUploadInteractionPrototype";
import DocumentPreviewModal from "./DocumentPreviewModal";
import type { PreviewDocumentInfo } from "./DocumentPreviewModal";
import type {
  DocumentProcessingRun,
  DocumentProcessingRunLookup,
} from "@src/packages/documents/ingestion/documentProcessingRun";
import {
  DocumentRow,
  DocumentTestResultRow,
  type DocumentRowStatus,
} from "./documents/DocumentRow";
import type { ResultInterpretation } from "@src/packages/homepage/plan/intelligence/useTestResultInsights";
import {
  categorizableFromTestResult,
  type ResultCategory,
} from "@src/packages/test-results/result-category";
import {
  useResultCategoryResolver,
  type ResultCategoryResolver,
} from "@src/packages/test-results/hooks/useResultCategory";
import { getDevClinicalDataState } from "@src/packages/care/dev-clinical-data-state";
import { useAuthorizedExperienceProjection } from "@src/packages/experience/hooks/useAuthorizedExperienceProjection";
import { filterDocumentSlotsForExperience } from "./documentsExperience";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
// ── Types ──

type DocumentStatus = "not_started" | "have_it" | "requested";

export type DocumentCategory =
  "medical_records" | "coverage_benefits" | "legal_consent" | "admin";

export type DocumentFilterCategory = DocumentCategory | "uncategorized";

/** A clinical value extracted from a parsed document (e.g. AMH: 3.2 ng/mL) */
type ClinicalValue = {
  label: string; // e.g. "AMH", "FSH", "TSH"
  value: string; // e.g. "3.2", "6.8", "Normal"
  unit?: string; // e.g. "ng/mL", "mIU/mL"
  referenceRange?: string; // e.g. "1.0–3.5"
  /** From the shared rule (`resolveResultCategory`); null when unplaced. */
  category: ResultCategory | null;
};

type DocumentSlot = {
  id: string;
  nameKey: string;
  whyKey: string;
  category: DocumentCategory;
  treatmentIntents: TreatmentIntent[] | null;
  partnerChoices: PartnerChoice[] | null;
  conditions: Condition[] | null;
  priority: "essential" | "recommended" | "optional";
  /** When set, this document is tied to a specific treatment cycle */
  cycleId?: string | null;
  /** The date the record/test was actually performed (not upload date) */
  recordDate?: string | null;
  /** Clinical values extracted from the uploaded document */
  clinicalValues?: ClinicalValue[] | null;
};

const DOCUMENT_CYCLE_TAG_PREFIX = "cycle:";
const DOCUMENT_CURRENT_CYCLE_TAG = "cycle:current";
const DOCUMENT_ACTION_NEEDED_FILTER_ID = "action-needed";
const DOCUMENT_UPLOADED_BY_USER_TAG = "uploaded-by:user";

type DocumentFilterOption = {
  id: string;
  label: string;
  icon: LucideIcon;
};

function getCycleTagFromDocument(doc: { tags: string[] }) {
  return doc.tags.find((tag) => tag.startsWith(DOCUMENT_CYCLE_TAG_PREFIX));
}

function getCycleTypeLabel(type: string, t: TranslationFunction) {
  switch (type) {
    case "ivf":
      return t("documents.cycle_type.ivf");
    case "fet":
      return t("documents.cycle_type.fet");
    case "egg-freezing":
    case "egg_freezing":
      return t("documents.cycle_type.egg_freezing");
    case "embryo-freezing":
    case "embryo_freezing":
      return t("documents.cycle_type.embryo_freezing");
    case "sperm-freezing":
    case "sperm_freezing":
      return t("documents.cycle_type.sperm_freezing");
    case "iui":
      return t("documents.cycle_type.iui");
    default:
      return type
        .split("-")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
  }
}

function parseCycleTag(tag: string) {
  const raw = tag.slice(DOCUMENT_CYCLE_TAG_PREFIX.length);
  const match = raw.match(/^(.+)-(\d+)$/);
  if (!match) return { type: raw, index: null };
  return { type: match[1]!, index: Number(match[2]) };
}

function formatCycleFilterLabel(
  tag: string,
  countByType: Record<string, number>,
  t: TranslationFunction,
) {
  const parsed = parseCycleTag(tag);
  const base = getCycleTypeLabel(parsed.type, t);
  if (parsed.type === "ivf" || parsed.type === "fet" || parsed.type === "iui") {
    return parsed.index ? `${base}${parsed.index}` : base;
  }
  if (parsed.index && (countByType[parsed.type] ?? 0) > 1) {
    return `${base} ${parsed.index}`;
  }
  return base;
}

function isDocumentRequested(doc: {
  uploads: { uploadStatus: "pending" | "uploaded" | null }[];
}) {
  return doc.uploads.some((upload) => upload.uploadStatus === "pending");
}

function buildDocumentFilterOptions(
  documents: Document[] | undefined,
  t: TranslationFunction,
): DocumentFilterOption[] {
  const options: DocumentFilterOption[] = [
    { id: "all", label: t("documents.filter.all"), icon: Layers },
  ];
  if (!documents || documents.length === 0) return options;

  if (documents.some(isDocumentRequested)) {
    options.push({
      id: DOCUMENT_ACTION_NEEDED_FILTER_ID,
      label: t("documents.filter.action_needed"),
      icon: AlertCircle,
    });
  }

  if (documents.some((doc) => doc.tags.includes(DOCUMENT_CURRENT_CYCLE_TAG))) {
    options.push({
      id: DOCUMENT_CURRENT_CYCLE_TAG,
      label: t("documents.filter.current_cycle"),
      icon: Calendar,
    });
  }

  const cycleTags = Array.from(
    new Set(documents.map(getCycleTagFromDocument).filter(Boolean) as string[]),
  ).filter((tag) => tag !== DOCUMENT_CURRENT_CYCLE_TAG);
  const countByType: Record<string, number> = {};
  for (const tag of cycleTags) {
    const { type } = parseCycleTag(tag);
    countByType[type] = (countByType[type] ?? 0) + 1;
  }
  cycleTags
    .sort((a, b) => a.localeCompare(b))
    .forEach((tag) => {
      options.push({
        id: tag,
        label: formatCycleFilterLabel(tag, countByType, t),
        icon: Calendar,
      });
    });

  return options;
}

function documentMatchesFilter(
  doc: Document | undefined,
  filterId: string,
  status?: DocumentStatus,
) {
  if (filterId === "all") return true;
  if (!doc) return false;
  if (filterId.startsWith(DOCUMENT_CYCLE_TAG_PREFIX)) {
    return doc.tags.includes(filterId);
  }
  if (filterId === "requested") {
    return status === "requested" || isDocumentRequested(doc);
  }
  if (filterId === DOCUMENT_ACTION_NEEDED_FILTER_ID) {
    return (
      status === "requested" ||
      status === "not_started" ||
      isDocumentRequested(doc)
    );
  }
  return true;
}

type CategoryMeta = {
  key: DocumentCategory;
  labelKey: string;
  icon: LucideIcon;
  image: number;
  color: string;
  descriptionKey: string;
};

type CategoryFilterMeta = Omit<CategoryMeta, "key" | "image"> & {
  key: DocumentFilterCategory;
  image?: number;
};

// ── Categories ──

export const CATEGORIES: CategoryMeta[] = [
  {
    key: "medical_records",
    labelKey: "documents.category.medical_records.label",
    icon: FileText,
    image: require("./care/assets/icon-folder.webp"),
    color: EMBER.colors.coral,
    descriptionKey: "documents.category.medical_records.description",
  },
  {
    key: "coverage_benefits",
    labelKey: "documents.category.coverage_benefits.label",
    icon: ShieldCheck,
    image: require("./care/assets/icon-shield.webp"),
    color: EMBER.colors.green,
    descriptionKey: "documents.category.coverage_benefits.description",
  },
  {
    key: "admin",
    labelKey: "documents.category.admin.label",
    icon: File,
    image: require("./care/assets/icon-receipt.webp"),
    color: EMBER.colors.gold,
    descriptionKey: "documents.category.admin.description",
  },
  {
    key: "legal_consent",
    labelKey: "documents.category.legal_consent.label",
    icon: ClipboardList,
    image: require("./care/assets/icon-signature.webp"),
    color: EMBER.colors.warm,
    descriptionKey: "documents.category.legal_consent.description",
  },
];

const UNCATEGORIZED_CATEGORY: CategoryFilterMeta = {
  key: "uncategorized",
  labelKey: "documents.category.uncategorized.label",
  icon: File,
  color: EMBER.colors.textFaint,
  descriptionKey: "documents.category.uncategorized.description",
};

const GROUPED_CATEGORY_ORDER: DocumentFilterCategory[] = [
  "medical_records",
  "coverage_benefits",
  "admin",
  "legal_consent",
  "uncategorized",
];

const DOCUMENT_CATEGORY_ALIASES: Record<string, DocumentFilterCategory> = {
  medical: "medical_records",
  medical_records: "medical_records",
  "medical records": "medical_records",
  coverage: "coverage_benefits",
  coverage_benefits: "coverage_benefits",
  "coverage & benefits": "coverage_benefits",
  "insurance & coverage": "coverage_benefits",
  insurance: "coverage_benefits",
  legal: "legal_consent",
  legal_consent: "legal_consent",
  "legal & consent": "legal_consent",
  "forms & agreements": "legal_consent",
  forms: "legal_consent",
  admin: "admin",
  "bills & receipts": "admin",
  "bills & payments": "admin",
  bills: "admin",
  uncategorized: "uncategorized",
};

function normalizeDocumentCategory(
  value: string | null | undefined,
): DocumentFilterCategory | null {
  if (!value) return null;
  return DOCUMENT_CATEGORY_ALIASES[value.trim().toLowerCase()] ?? null;
}

// ── Document Slots ──

const ALL_EXCEPT_EXPLORING: TreatmentIntent[] = [
  "Preservation",
  "Donation",
  "Conception",
  "StartingTreatment",
  "InTreatment",
];

const TREATMENT_ACTIVE: TreatmentIntent[] = [
  "Conception",
  "StartingTreatment",
  "InTreatment",
];

export const DOCUMENT_SLOTS: DocumentSlot[] = [
  // ── Medical Records ──
  {
    id: "amh",
    nameKey: "documents.slot.amh.name",
    whyKey: "documents.slot.amh.why",
    category: "medical_records",
    treatmentIntents: null,
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },
  {
    id: "day3-panel",
    nameKey: "documents.slot.day3_panel.name",
    whyKey: "documents.slot.day3_panel.why",
    category: "medical_records",
    treatmentIntents: ALL_EXCEPT_EXPLORING,
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },
  {
    id: "afc-ultrasound",
    nameKey: "documents.slot.afc_ultrasound.name",
    whyKey: "documents.slot.afc_ultrasound.why",
    category: "medical_records",
    treatmentIntents: ALL_EXCEPT_EXPLORING,
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },
  {
    id: "hsg-saline",
    nameKey: "documents.slot.hsg_saline.name",
    whyKey: "documents.slot.hsg_saline.why",
    category: "medical_records",
    treatmentIntents: TREATMENT_ACTIVE,
    partnerChoices: null,
    conditions: null,
    priority: "recommended",
  },
  {
    id: "carrier-screening",
    nameKey: "documents.slot.carrier_screening.name",
    whyKey: "documents.slot.carrier_screening.why",
    category: "medical_records",
    treatmentIntents: [
      "Conception",
      "Preservation",
      "StartingTreatment",
      "InTreatment",
    ],
    partnerChoices: null,
    conditions: null,
    priority: "recommended",
  },
  {
    id: "pgt-results",
    nameKey: "documents.slot.pgt_results.name",
    whyKey: "documents.slot.pgt_results.why",
    category: "medical_records",
    treatmentIntents: ["StartingTreatment", "InTreatment"],
    partnerChoices: null,
    conditions: null,
    priority: "recommended",
  },
  {
    id: "semen-analysis",
    nameKey: "documents.slot.semen_analysis.name",
    whyKey: "documents.slot.semen_analysis.why",
    category: "medical_records",
    treatmentIntents: [
      "Preservation",
      "Conception",
      "StartingTreatment",
      "InTreatment",
    ],
    partnerChoices: ["with-partner"],
    conditions: null,
    priority: "essential",
  },
  {
    id: "thyroid-panel",
    nameKey: "documents.slot.thyroid_panel.name",
    whyKey: "documents.slot.thyroid_panel.why",
    category: "medical_records",
    treatmentIntents: null,
    partnerChoices: null,
    conditions: ["thyroid"],
    priority: "essential",
  },
  {
    id: "pcos-labs",
    nameKey: "documents.slot.pcos_labs.name",
    whyKey: "documents.slot.pcos_labs.why",
    category: "medical_records",
    treatmentIntents: null,
    partnerChoices: null,
    conditions: ["pcos"],
    priority: "recommended",
  },
  {
    id: "endo-imaging",
    nameKey: "documents.slot.endo_imaging.name",
    whyKey: "documents.slot.endo_imaging.why",
    category: "medical_records",
    treatmentIntents: null,
    partnerChoices: null,
    conditions: ["endo"],
    priority: "recommended",
  },
  {
    id: "genetic-counseling",
    nameKey: "documents.slot.genetic_counseling.name",
    whyKey: "documents.slot.genetic_counseling.why",
    category: "medical_records",
    treatmentIntents: TREATMENT_ACTIVE,
    partnerChoices: null,
    conditions: null,
    priority: "optional",
  },
  {
    id: "treatment-plan",
    nameKey: "documents.slot.treatment_plan.name",
    whyKey: "documents.slot.treatment_plan.why",
    category: "medical_records",
    treatmentIntents: ["StartingTreatment", "InTreatment"],
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },
  {
    id: "prescription",
    nameKey: "documents.slot.prescription.name",
    whyKey: "documents.slot.prescription.why",
    category: "medical_records",
    treatmentIntents: null,
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },

  // ── Coverage & Benefits ──
  {
    id: "insurance-card",
    nameKey: "documents.slot.insurance_card.name",
    whyKey: "documents.slot.insurance_card.why",
    category: "coverage_benefits",
    treatmentIntents: null,
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },
  {
    id: "benefits-summary",
    nameKey: "documents.slot.benefits_summary.name",
    whyKey: "documents.slot.benefits_summary.why",
    category: "coverage_benefits",
    treatmentIntents: null,
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },
  {
    id: "pre-auth",
    nameKey: "documents.slot.pre_auth.name",
    whyKey: "documents.slot.pre_auth.why",
    category: "coverage_benefits",
    treatmentIntents: ["StartingTreatment", "InTreatment"],
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },
  {
    id: "eob",
    nameKey: "documents.slot.eob.name",
    whyKey: "documents.slot.eob.why",
    category: "coverage_benefits",
    treatmentIntents: ALL_EXCEPT_EXPLORING,
    partnerChoices: null,
    conditions: null,
    priority: "recommended",
  },
  {
    id: "appeal-letter",
    nameKey: "documents.slot.appeal_letter.name",
    whyKey: "documents.slot.appeal_letter.why",
    category: "coverage_benefits",
    treatmentIntents: ["StartingTreatment", "InTreatment"],
    partnerChoices: null,
    conditions: null,
    priority: "optional",
  },

  // ── Legal & Consent ──
  {
    id: "consent-forms",
    nameKey: "documents.slot.consent_forms.name",
    whyKey: "documents.slot.consent_forms.why",
    category: "legal_consent",
    treatmentIntents: ["StartingTreatment", "InTreatment", "Preservation"],
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },
  {
    id: "referral-letter",
    nameKey: "documents.slot.referral_letter.name",
    whyKey: "documents.slot.referral_letter.why",
    category: "legal_consent",
    treatmentIntents: ALL_EXCEPT_EXPLORING,
    partnerChoices: null,
    conditions: null,
    priority: "recommended",
  },
  {
    id: "donor-agreement",
    nameKey: "documents.slot.donor_agreement.name",
    whyKey: "documents.slot.donor_agreement.why",
    category: "legal_consent",
    treatmentIntents: TREATMENT_ACTIVE,
    partnerChoices: ["using-donor"],
    conditions: null,
    priority: "essential",
  },
  {
    id: "partner-consent",
    nameKey: "documents.slot.partner_consent.name",
    whyKey: "documents.slot.partner_consent.why",
    category: "legal_consent",
    treatmentIntents: [
      "Conception",
      "Preservation",
      "StartingTreatment",
      "InTreatment",
    ],
    partnerChoices: ["with-partner"],
    conditions: null,
    priority: "essential",
  },
  {
    id: "storage-agreement",
    nameKey: "documents.slot.storage_agreement.name",
    whyKey: "documents.slot.storage_agreement.why",
    category: "legal_consent",
    treatmentIntents: ["Preservation", "InTreatment"],
    partnerChoices: null,
    conditions: null,
    priority: "recommended",
  },
  {
    id: "disposition-directive",
    nameKey: "documents.slot.disposition_directive.name",
    whyKey: "documents.slot.disposition_directive.why",
    category: "legal_consent",
    treatmentIntents: ["Preservation", "InTreatment"],
    partnerChoices: null,
    conditions: null,
    priority: "recommended",
  },

  // ── Bills & Payments ──
  {
    id: "clinic-invoices",
    nameKey: "documents.slot.clinic_invoices.name",
    whyKey: "documents.slot.clinic_invoices.why",
    category: "admin",
    treatmentIntents: ALL_EXCEPT_EXPLORING,
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },
  {
    id: "pharmacy-receipts",
    nameKey: "documents.slot.pharmacy_receipts.name",
    whyKey: "documents.slot.pharmacy_receipts.why",
    category: "admin",
    treatmentIntents: ["StartingTreatment", "InTreatment", "Preservation"],
    partnerChoices: null,
    conditions: null,
    priority: "essential",
  },
  {
    id: "storage-invoices",
    nameKey: "documents.slot.storage_invoices.name",
    whyKey: "documents.slot.storage_invoices.why",
    category: "admin",
    treatmentIntents: ["Preservation", "InTreatment"],
    partnerChoices: null,
    conditions: null,
    priority: "recommended",
  },
  {
    id: "lab-bills",
    nameKey: "documents.slot.lab_bills.name",
    whyKey: "documents.slot.lab_bills.why",
    category: "admin",
    treatmentIntents: ALL_EXCEPT_EXPLORING,
    partnerChoices: null,
    conditions: null,
    priority: "recommended",
  },
  {
    id: "travel-receipts",
    nameKey: "documents.slot.travel_receipts.name",
    whyKey: "documents.slot.travel_receipts.why",
    category: "admin",
    treatmentIntents: null,
    partnerChoices: null,
    conditions: null,
    priority: "optional",
  },
];

const PRIORITY_ORDER = { essential: 0, recommended: 1, optional: 2 };
const DOCUMENT_SLOT_ORDER = (a: DocumentSlot, b: DocumentSlot) =>
  PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];

function deriveStatuses(
  documents:
    | {
        tags: string[];
        uploads: { uploadStatus: "pending" | "uploaded" | null }[];
      }[]
    | undefined,
  slotIds: string[],
): Record<string, DocumentStatus> {
  const statuses: Record<string, DocumentStatus> = {};
  if (!documents) return statuses;

  for (const slotId of slotIds) {
    const matchingDoc = documents.find((doc) => doc.tags.includes(slotId));
    if (!matchingDoc) {
      statuses[slotId] = "not_started";
    } else if (matchingDoc.uploads.some((u) => u.uploadStatus === "uploaded")) {
      statuses[slotId] = "have_it";
    } else if (matchingDoc.uploads.length > 0) {
      statuses[slotId] = "requested";
    } else {
      statuses[slotId] = "not_started";
    }
  }
  return statuses;
}

// ── Components ──

function DocumentsEmptyState({
  variant,
  onUpload,
  onFilesSelected,
  onClearFilters,
}: {
  variant: "empty" | "filtered";
  onUpload: () => void;
  onFilesSelected: (files: SelectedFile[]) => void;
  onClearFilters: () => void;
}) {
  const t = useTranslations();
  const isFiltered = variant === "filtered";

  if (isFiltered) {
    return (
      <SearchEmptyState
        title={t("documents.empty.no_matches")}
        subtitle={t("documents.empty.adjust_filters")}
        onReset={onClearFilters}
      />
    );
  }

  return (
    <View
      style={{
        paddingVertical: 8,
        alignItems: "center",
        gap: 12,
      }}
    >
      <DocumentFileDropZone
        onPick={onUpload}
        onFilesSelected={onFilesSelected}
        dropHint={t("documents.empty.or_drop_files_here")}
      >
        <EmptyState
          density="compact"
          titleVariant="featured"
          illustration={
            <DialogIcon
              source={documentsIllustration}
              width={68}
              height={68}
              // Trim the square asset's transparent vertical inset from layout
              // so the visible artwork shares the subtitle-to-action gap.
              style={{ marginTop: -6, marginBottom: -7 }}
              testID="documents-empty-illustration"
            />
          }
          title={t("documents.empty.your_documents_in_one_place")}
          subtitle={t("documents.upload.add_any_file_to_your_care_record")}
          subtitleStyle={{ maxWidth: 410 }}
          style={{ paddingVertical: 0 }}
          action={
            <CompactActionButton
              label={t("documents.empty.upload_documents")}
              action="documents.empty.upload_documents"
              leadingIcon={Upload}
              variant="primary"
              onPress={onUpload}
            />
          }
        />
      </DocumentFileDropZone>
    </View>
  );
}

// ── Main Screen ──

type Props = NativeStackScreenProps<CareStackParamList, "Documents">;

/** Map test results to document slots via shared upload IDs */
function buildSlotTestResults(
  documents:
    | {
        tags: string[];
        uploads: { id: string; uploadStatus: "pending" | "uploaded" | null }[];
      }[]
    | undefined,
  testResults: TestResult[] | undefined,
  categorize: ResultCategoryResolver["categorize"],
): Record<string, ClinicalValue[]> {
  const map: Record<string, ClinicalValue[]> = {};
  if (!documents || !testResults) return map;

  // Build a lookup: uploadId → slotId
  const uploadToSlot: Record<string, string> = {};
  for (const doc of documents) {
    const slotTag = doc.tags[0];
    if (!slotTag) continue;
    for (const upload of doc.uploads) {
      if (upload.id) {
        uploadToSlot[upload.id] = slotTag;
      }
    }
  }

  // Match test results to slots via their attachment uploadId
  for (const tr of testResults) {
    const uploadId = tr.attachment?.uploadId;
    if (!uploadId) continue;
    const slotId = uploadToSlot[uploadId];
    if (!slotId) continue;

    // The same value and category every other results surface shows.
    const result = categorizableFromTestResult(tr);
    if (!result.value) continue;

    if (!map[slotId]) {
      map[slotId] = [];
    }
    map[slotId].push({
      label: tr.name ?? "",
      value: result.value,
      unit: tr.unit ?? undefined,
      category: categorize(result)?.category ?? null,
    });
  }

  return map;
}

/** Get the record date for a slot from its matching document */
function buildSlotDates(
  documents:
    | {
        tags: string[];
        documentDate: string | null;
        createdAt: string | null;
      }[]
    | undefined,
): Record<string, string | null> {
  const map: Record<string, string | null> = {};
  if (!documents) return map;
  for (const doc of documents) {
    const slotTag = doc.tags[0];
    if (!slotTag) continue;
    map[slotTag] = doc.documentDate ?? doc.createdAt ?? null;
  }
  return map;
}

function getDocumentRowStatus(status: DocumentStatus): DocumentRowStatus {
  if (status === "have_it") return "on-file";
  if (status === "requested") return "requested";
  return "missing";
}

function formatDocumentRowDate(
  date: string | null | undefined,
  t: TranslationFunction,
  locale: string,
) {
  if (!date) return t("documents.no_date");
  const normalized = date.includes("T") ? date : `${date}T00:00:00`;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return t("documents.no_date");
  const tag = locale === "fr" ? "fr-FR" : "en-US";
  return parsed.toLocaleDateString(tag, {
    month: "short",
    day: "numeric",
  });
}

function getClinicalValuePurpose(
  slot: DocumentSlot,
  value: ClinicalValue,
  t: TranslationFunction,
) {
  const normalized = `${value.label} ${slot.nameKey}`.toLowerCase();
  if (normalized.includes("amh")) {
    return t("documents.clinical_value.purpose.amh");
  }
  if (normalized.includes("fsh")) {
    return t("documents.clinical_value.purpose.fsh");
  }
  if (normalized.includes("e2") || normalized.includes("estradiol")) {
    return t("documents.clinical_value.purpose.e2");
  }
  if (normalized.includes("afc") || normalized.includes("follicle")) {
    return t("documents.clinical_value.purpose.afc");
  }
  if (normalized.includes("tsh") || normalized.includes("thyroid")) {
    return t("documents.clinical_value.purpose.tsh");
  }
  return slot.whyKey;
}

function clinicalValueToInsight(
  slot: DocumentSlot,
  value: ClinicalValue,
  t: TranslationFunction,
): ResultInterpretation {
  return {
    testName: value.label || slot.nameKey,
    testPurpose: getClinicalValuePurpose(slot, value, t),
    value: value.value,
    unit: value.unit ?? "",
    category: value.category,
    headline: value.label || slot.nameKey,
    interpretation: slot.whyKey,
    whatThisMeans: value.referenceRange
      ? t("documents.clinical_value.reference_range", {
          params: { range: value.referenceRange },
        })
      : slot.whyKey,
  };
}

type FlatDocumentItem =
  | {
      kind: "slot";
      id: string;
      category: DocumentCategory;
      slot: DocumentSlot;
      status: DocumentStatus;
      document?: Document;
      date: string | null;
      actionNeeded: boolean;
      searchText: string;
      sortLabel: string;
    }
  | {
      kind: "free";
      id: string;
      category: DocumentFilterCategory;
      document: Document;
      date: string | null;
      actionNeeded: boolean;
      searchText: string;
      sortLabel: string;
    };

function getCategoryMeta(category: DocumentFilterCategory): CategoryFilterMeta {
  return (
    CATEGORIES.find((cat) => cat.key === category) ?? UNCATEGORIZED_CATEGORY
  );
}

export function getDocumentCategoryFromTags(
  tags: string[],
): DocumentFilterCategory {
  const category = tags
    .map((tag) => normalizeDocumentCategory(tag))
    .find(
      (tag): tag is DocumentCategory => Boolean(tag) && tag !== "uncategorized",
    );
  return category ?? "uncategorized";
}

function compareFlatDocumentItems(a: FlatDocumentItem, b: FlatDocumentItem) {
  if (a.actionNeeded !== b.actionNeeded) return a.actionNeeded ? -1 : 1;
  const dateA = a.date ?? "";
  const dateB = b.date ?? "";
  if (dateA && dateB && dateA !== dateB) return dateB.localeCompare(dateA);
  if (dateA && !dateB) return -1;
  if (!dateA && dateB) return 1;
  return a.sortLabel.localeCompare(b.sortLabel);
}

function flatDocumentItemMatchesFilter(
  item: FlatDocumentItem,
  filterId: string,
) {
  if (filterId === DOCUMENT_ACTION_NEEDED_FILTER_ID) return item.actionNeeded;
  if (item.kind === "slot") {
    return documentMatchesFilter(item.document, filterId, item.status);
  }
  return documentMatchesFilter(
    item.document,
    filterId,
    item.actionNeeded ? "requested" : "have_it",
  );
}

function isMoonDocumentsPreviewEnabled() {
  if (!__DEV__ || typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return (
    params.get("dev_preview_documents") === "true" ||
    params.get("dev_preview_all_cards") === "true"
  );
}

function getDocumentUploadPrototypeState(): DocumentUploadPrototypeState | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  const state = new URLSearchParams(window.location.search).get(
    "dev_documents_upload_prototype",
  );
  return DOCUMENT_UPLOAD_PROTOTYPE_STATES.includes(
    state as DocumentUploadPrototypeState,
  )
    ? (state as DocumentUploadPrototypeState)
    : null;
}

function isMoonDocumentsEmptyPreviewEnabled() {
  if (!__DEV__ || typeof window === "undefined") return false;
  return (
    new URLSearchParams(window.location.search).get("dev_documents_state") ===
    "empty"
  );
}

function getMoonDocumentsPreviewCategory(): DocumentCategory | undefined {
  if (!__DEV__ || typeof window === "undefined") return undefined;
  const category = new URLSearchParams(window.location.search).get(
    "dev_documents_category",
  );
  if (
    category === "medical_records" ||
    category === "coverage_benefits" ||
    category === "legal_consent" ||
    category === "admin"
  ) {
    return category;
  }
  return undefined;
}

function createPreviewDocument(
  id: string,
  name: string,
  tags: string[],
  options: {
    documentDate: string;
    uploadStatus?: "pending" | "uploaded";
    uploadName?: string;
  },
): Document {
  const uploadId = `${id}-upload`;
  return {
    id,
    patientId: "dev-patient",
    name,
    documentDate: options.documentDate,
    tags,
    sharedWith: ["patient"],
    createdBy: "dev-preview",
    createdAt: `${options.documentDate}T09:00:00.000Z`,
    updatedBy: null,
    updatedAt: null,
    uploads: [
      {
        id: uploadId,
        name: options.uploadName ?? `${name}.pdf`,
        url: null,
        uploadStatus: options.uploadStatus ?? "uploaded",
        contentType: "application/pdf",
        size: "512000",
        notes: null,
        createdAt: `${options.documentDate}T09:00:00.000Z`,
      },
    ],
  };
}

function createPreviewTestResult(
  id: string,
  uploadId: string,
  name: string,
  value: string,
  unit: string | null,
  assessment: TestResult["clinicalAssessment"],
): TestResult {
  return {
    id,
    patientId: "dev-patient",
    phaseId: null,
    cycleId: "dev-cycle",
    name,
    type: "blood",
    bloodTestCategory: "hormonal",
    urineTestCategory: null,
    geneticsTestCategory: null,
    imagingTestCategory: null,
    samplingTestCategory: null,
    clinicalTestCategory: null,
    semenFluidTestCategory: null,
    biopsyTestCategory: null,
    otherCategory: null,
    patientValue: value,
    doctorValue: null,
    value,
    valueType: "numeric",
    unit,
    date: "2026-05-19",
    notes: null,
    patientAssessmentNormal: assessment === "normal",
    clinicalAssessment: assessment,
    clinicalAssessmentDetails: null,
    loincCode: null,
    providerId: null,
    ownership: "patient",
    ownerId: "dev-patient",
    sourceId: null,
    audience: ["patient"],
    createdAt: "2026-05-19T09:00:00.000Z",
    updatedAt: "2026-05-19T09:00:00.000Z",
    updatedBy: null,
    source: "patient",
    verificationStatus: "unverified",
    verifiedAt: null,
    verifiedBy: null,
    verificationNotes: null,
    attachment: {
      id: `${id}-attachment`,
      testResultId: id,
      uploadId,
      createdAt: "2026-05-19T09:00:00.000Z",
      updatedAt: "2026-05-19T09:00:00.000Z",
      updatedBy: null,
      upload: {
        id: uploadId,
        name: `${name}.pdf`,
        contentDigest: null,
        url: null,
        uploadStatus: "uploaded",
        contentType: "application/pdf",
        size: "512000",
        notes: null,
        ownership: "patient",
        ownerId: "dev-patient",
        sourceId: null,
        audience: ["patient"],
        createdAt: "2026-05-19T09:00:00.000Z",
        updatedAt: "2026-05-19T09:00:00.000Z",
        updatedBy: null,
      },
    },
    phase: null,
  };
}

const MOON_DOCUMENTS_PREVIEW_DOCUMENTS: Document[] = [
  createPreviewDocument(
    "doc-amh",
    "AMH blood test",
    ["amh", "medical_records", "doctype:lab", DOCUMENT_CURRENT_CYCLE_TAG],
    {
      documentDate: "2026-05-12",
    },
  ),
  createPreviewDocument(
    "doc-day3",
    "Day 3 hormone panel",
    [
      "day3-panel",
      "medical_records",
      "doctype:lab",
      DOCUMENT_CURRENT_CYCLE_TAG,
    ],
    {
      documentDate: "2026-05-10",
    },
  ),
  createPreviewDocument(
    "doc-afc",
    "AFC ultrasound report",
    [
      "afc-ultrasound",
      "medical_records",
      "doctype:imaging",
      DOCUMENT_CURRENT_CYCLE_TAG,
    ],
    {
      documentDate: "2026-05-10",
    },
  ),
  createPreviewDocument(
    "doc-treatment-plan",
    "Treatment plan / protocol",
    [
      "treatment-plan",
      "medical_records",
      "doctype:treatment_plan",
      DOCUMENT_CURRENT_CYCLE_TAG,
      "med:Gonal-F|150 IU|Daily|May 16|8 PM",
      "med:Cetrotide|0.25 mg|Daily|May 19|8 AM",
      "med:Menopur|75 IU|Daily|May 16|8 PM",
      "appt:Monitoring ultrasound|Monitoring|May 20",
      "appt:Trigger instruction call|Care team|May 22",
    ],
    {
      documentDate: "2026-05-16",
    },
  ),
  createPreviewDocument(
    "doc-carrier",
    "Carrier screening results",
    ["carrier-screening", "medical_records", "doctype:genetic", "cycle:ivf-1"],
    {
      documentDate: "2026-05-18",
      uploadStatus: "pending",
    },
  ),
  createPreviewDocument(
    "doc-insurance-card",
    "Insurance card",
    ["insurance-card", "coverage_benefits"],
    {
      documentDate: "2026-04-20",
    },
  ),
  createPreviewDocument(
    "doc-benefits",
    "Fertility benefits summary",
    ["benefits-summary", "coverage_benefits"],
    {
      documentDate: "2026-04-22",
    },
  ),
  createPreviewDocument(
    "doc-preauth",
    "Pre-authorization letter",
    ["pre-auth", "coverage_benefits", "cycle:ivf-1"],
    {
      documentDate: "2026-05-17",
      uploadStatus: "pending",
    },
  ),
  createPreviewDocument(
    "doc-consent",
    "Treatment consent forms",
    ["consent-forms", "legal_consent", DOCUMENT_CURRENT_CYCLE_TAG],
    {
      documentDate: "2026-05-15",
    },
  ),
  createPreviewDocument(
    "doc-partner-consent",
    "Partner consent forms",
    ["partner-consent", "legal_consent", "cycle:ivf-1"],
    {
      documentDate: "2026-05-18",
      uploadStatus: "pending",
    },
  ),
  createPreviewDocument(
    "doc-storage-agreement",
    "Cryostorage agreement",
    ["storage-agreement", "legal_consent", DOCUMENT_CURRENT_CYCLE_TAG],
    {
      documentDate: "2026-05-13",
    },
  ),
  createPreviewDocument(
    "doc-clinic-invoice",
    "Clinic invoice",
    ["clinic-invoices", "admin", DOCUMENT_CURRENT_CYCLE_TAG],
    {
      documentDate: "2026-05-11",
    },
  ),
  createPreviewDocument(
    "doc-pharmacy-receipt",
    "Pharmacy receipt",
    ["pharmacy-receipts", "admin", DOCUMENT_CURRENT_CYCLE_TAG],
    {
      documentDate: "2026-05-16",
    },
  ),
  createPreviewDocument(
    "doc-lab-bill",
    "Outside lab bill",
    ["lab-bills", "admin", DOCUMENT_UPLOADED_BY_USER_TAG],
    {
      documentDate: "2026-05-03",
    },
  ),
  createPreviewDocument(
    "doc-free-coverage",
    "Progyny authorization packet",
    ["coverage_benefits", DOCUMENT_UPLOADED_BY_USER_TAG],
    {
      documentDate: "2026-05-09",
    },
  ),
];

const MOON_DOCUMENTS_PREVIEW_TEST_RESULTS: TestResult[] = [
  createPreviewTestResult(
    "result-amh",
    "doc-amh-upload",
    "AMH",
    "2.8",
    "ng/mL",
    "normal",
  ),
  createPreviewTestResult(
    "result-fsh",
    "doc-day3-upload",
    "FSH",
    "8.1",
    "mIU/mL",
    "normal",
  ),
  createPreviewTestResult(
    "result-afc",
    "doc-afc-upload",
    "AFC",
    "15",
    "follicles",
    "normal",
  ),
];

/**
 * How the preview documents were read, so their values can be seen and
 * reviewed without an upload job behind them. The AMH test's values still
 * wait for the patient; the day 3 panel's were confirmed.
 */
function createPreviewProcessingLookup(
  documentId: string,
  values: {
    label: string;
    value: string;
    unit: string;
  }[],
  status: "proposed" | "accepted",
): DocumentProcessingRunLookup {
  const runId = `${documentId}-run`;
  const patientId = "dev-patient";
  const sourceEntityId = `${documentId}-source`;
  const claims: DocumentProcessingRun["claims"] = values.map(
    (entry, ordinal) => ({
      id: `${documentId}-claim-${ordinal}`,
      runId,
      patientId,
      sourceEntityId,
      ordinal,
      claimHash: `${documentId}-claim-hash-${ordinal}`,
      kind: "number",
      fieldPath: `results[${ordinal}].value`,
      rawLabel: entry.label,
      rawValue: entry.value,
      rawDate: null,
      rawUnit: entry.unit,
      normalizedValue: null,
      status,
      statusReason: status === "accepted" ? "Confirmed by the patient." : null,
      confidence: 0.96,
      anomalies: [],
      version: status === "accepted" ? 2 : 1,
      evidence: [],
    }),
  );
  return {
    state: "ready",
    patientDocumentId: documentId,
    sourceEntityId,
    run: {
      id: runId,
      patientId,
      sourceEntityId,
      uploadId: `${documentId}-upload`,
      uploadJobId: `${documentId}-job`,
      fileItemId: `${documentId}-item`,
      sourceChannel: "patient_ui",
      intent: "dump_anything",
      status: "succeeded",
      completeness: "complete",
      classificationCode: "lab_result",
      classificationConfidence: 0.97,
      classificationEvidence: null,
      pagesSeen: 1,
      omittedReason: null,
      parserName: "dev-preview",
      parserVersion: "1",
      modelName: "dev-preview",
      modelVersion: "1",
      errorCode: null,
      errorDetail: null,
      retryOfRunId: null,
      startedAt: "2026-05-12T09:00:00.000Z",
      completedAt: "2026-05-12T09:00:04.000Z",
      segments: [],
      evidence: [],
      parties: [],
      claims,
    },
    serverTime: "2026-05-12T09:00:05.000Z",
  };
}

const MOON_DOCUMENTS_PREVIEW_PROCESSING: Record<
  string,
  DocumentProcessingRunLookup
> = {
  "doc-amh": createPreviewProcessingLookup(
    "doc-amh",
    [
      { label: "AMH", value: "2.1", unit: "ng/mL" },
      { label: "FSH", value: "6.4", unit: "mIU/mL" },
      { label: "Estradiol", value: "42", unit: "pg/mL" },
    ],
    "proposed",
  ),
  "doc-day3": createPreviewProcessingLookup(
    "doc-day3",
    [
      { label: "FSH", value: "8.1", unit: "mIU/mL" },
      { label: "LH", value: "5.2", unit: "mIU/mL" },
      { label: "Estradiol", value: "38", unit: "pg/mL" },
    ],
    "accepted",
  ),
};

const EMPTY_DOCUMENTS_PREVIEW: Document[] = [];
const EMPTY_TEST_RESULTS_PREVIEW: TestResult[] = [];

export default function DocumentsScreen({ navigation, route }: Props) {
  const capturePress = usePressCapture("DocumentsScreen");
  const visualEvidenceRoute =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("dev_visual_evidence") ===
      "true";
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Care",
    fallbackScreen: "CareOverview",
  });
  const t = useTranslations();
  const { locale } = useLocale();
  const documentsQuery = useDocuments();
  const {
    data: documents,
    isLoading: documentsLoading,
    error: documentsError,
  } = documentsQuery;
  const testResultsQuery = useTestResults();
  const { data: testResults, error: testResultsError } = testResultsQuery;
  const authorizedExperience = useAuthorizedExperienceProjection();
  const isDocumentsPreview = isMoonDocumentsPreviewEnabled();
  const documentUploadPrototypeState = getDocumentUploadPrototypeState();
  const devClinicalDataState = getDevClinicalDataState("documents");
  const documentsDataState = devClinicalDataState
    ? devClinicalDataState
    : isDocumentsPreview
      ? null
      : documentsError && documents === undefined
        ? "unavailable"
        : documentsError || testResultsError
          ? "stale"
          : null;
  const isRetryingDocuments =
    documentsQuery.isFetching || testResultsQuery.isFetching;
  const retryDocuments = () => {
    void documentsQuery.refetch();
    void testResultsQuery.refetch();
  };
  const isEmptyDocumentsPreview =
    isDocumentsPreview && isMoonDocumentsEmptyPreviewEnabled();
  const displayDocuments = useMemo(() => {
    if (!isDocumentsPreview) return documents;
    return isEmptyDocumentsPreview
      ? EMPTY_DOCUMENTS_PREVIEW
      : MOON_DOCUMENTS_PREVIEW_DOCUMENTS;
  }, [documents, isDocumentsPreview, isEmptyDocumentsPreview]);
  const displayTestResults = useMemo(() => {
    if (!isDocumentsPreview) return testResults;
    return isEmptyDocumentsPreview
      ? EMPTY_TEST_RESULTS_PREVIEW
      : MOON_DOCUMENTS_PREVIEW_TEST_RESULTS;
  }, [isDocumentsPreview, isEmptyDocumentsPreview, testResults]);
  const displayDocumentsLoading = isDocumentsPreview ? false : documentsLoading;
  const initialCategory = route.params?.initialCategory;
  const initialDocumentId = route.params?.initialDocumentId;
  const initialDocumentFilter = route.params?.initialDocumentFilter;
  const initialDocumentFilterLabel =
    route.params?.initialDocumentFilterLabel ??
    t("documents.filter.cycle_documents_fallback");
  const previewInitialCategory = isDocumentsPreview
    ? getMoonDocumentsPreviewCategory()
    : undefined;
  const initialCategoryFilter = useMemo(
    () => normalizeDocumentCategory(previewInitialCategory ?? initialCategory),
    [initialCategory, previewInitialCategory],
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [activeDocumentFilter, setActiveDocumentFilter] = useState(
    initialDocumentFilter ?? "all",
  );
  const [activeCategoryFilters, setActiveCategoryFilters] = useState<
    DocumentFilterCategory[]
  >(() => (initialCategoryFilter ? [initialCategoryFilter] : []));
  const [uploadModalVisible, setUploadModalVisible] = useState(
    route.params?.openUpload === true,
  );
  const [uploadSlot, setUploadSlot] = useState<DocumentSlotInfo>(null);
  const [pendingUploadFiles, setPendingUploadFiles] = useState<SelectedFile[]>(
    [],
  );
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewDocumentInfo, setPreviewDocumentInfo] =
    useState<PreviewDocumentInfo>(null);
  const openedInitialDocumentId = useRef<string | null>(null);
  // The preview's documents carry how they were read; a real document's is
  // looked up by the preview modal itself.
  const previewProcessingLookup = useCallback(
    (documentId: string) =>
      isDocumentsPreview
        ? (MOON_DOCUMENTS_PREVIEW_PROCESSING[documentId] ?? null)
        : undefined,
    [isDocumentsPreview],
  );
  const uploadButtonDrift = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (route.params?.openUpload === true) {
      setUploadModalVisible(true);
    }
  }, [route.params?.openUpload]);

  useEffect(() => {
    if (!initialDocumentId) {
      openedInitialDocumentId.current = null;
      return;
    }
    if (
      !displayDocuments ||
      openedInitialDocumentId.current === initialDocumentId
    ) {
      return;
    }

    const document = displayDocuments.find(
      (item) => item.id === initialDocumentId,
    );
    if (!document) return;

    const slot = DOCUMENT_SLOTS.find((item) => document.tags.includes(item.id));
    setPreviewDocumentInfo({
      slotId: slot?.id ?? document.id,
      slotName: slot ? t(slot.nameKey) : document.name,
      slotWhy: slot ? t(slot.whyKey) : "",
      document,
      processingLookup: previewProcessingLookup(document.id),
    });
    setPreviewModalVisible(true);
    openedInitialDocumentId.current = initialDocumentId;
  }, [displayDocuments, initialDocumentId, previewProcessingLookup, t]);

  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (reducedMotion) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(uploadButtonDrift, {
          toValue: 1,
          duration: 7000,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
        Animated.timing(uploadButtonDrift, {
          toValue: 0,
          duration: 7000,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [uploadButtonDrift, reducedMotion]);

  const filteredSlots = useMemo(
    () =>
      filterDocumentSlotsForExperience(DOCUMENT_SLOTS, {
        status: authorizedExperience.status,
        context: authorizedExperience.projection?.context ?? null,
        capability: authorizedExperience.canonical?.capability ?? null,
      }).sort(DOCUMENT_SLOT_ORDER),
    [
      authorizedExperience.canonical?.capability,
      authorizedExperience.projection?.context,
      authorizedExperience.status,
    ],
  );
  const allSlotIds = useMemo(
    () => new Set(DOCUMENT_SLOTS.map((s) => s.id)),
    [],
  );
  const documentFilterOptions = useMemo(() => {
    const options = buildDocumentFilterOptions(displayDocuments, t);
    if (
      initialDocumentFilter &&
      !options.some((option) => option.id === initialDocumentFilter)
    ) {
      return [
        options[0] ?? {
          id: "all",
          label: t("documents.filter.all"),
          icon: Layers,
        },
        {
          id: initialDocumentFilter,
          label: initialDocumentFilterLabel,
          icon: Calendar,
        },
        ...options.slice(1),
      ];
    }
    return options;
  }, [displayDocuments, initialDocumentFilter, initialDocumentFilterLabel, t]);
  const primaryDocumentFilterOptions = useMemo(
    () =>
      documentFilterOptions.filter(
        (option) =>
          option.id === "all" || option.id === DOCUMENT_ACTION_NEEDED_FILTER_ID,
      ),
    [documentFilterOptions],
  );
  const contextualDocumentFilterOptions = useMemo(
    () =>
      documentFilterOptions.filter(
        (option) =>
          option.id !== "all" && option.id !== DOCUMENT_ACTION_NEEDED_FILTER_ID,
      ),
    [documentFilterOptions],
  );

  useEffect(() => {
    setActiveDocumentFilter(initialDocumentFilter ?? "all");
  }, [initialDocumentFilter]);

  useEffect(() => {
    setActiveCategoryFilters(
      initialCategoryFilter ? [initialCategoryFilter] : [],
    );
  }, [initialCategoryFilter]);

  const defaultUploadTags = useMemo(
    () =>
      activeDocumentFilter.startsWith(DOCUMENT_CYCLE_TAG_PREFIX)
        ? [activeDocumentFilter]
        : [],
    [activeDocumentFilter],
  );
  const uploadSlotCategory = useMemo(
    () => filteredSlots.find((slot) => slot.id === uploadSlot?.id)?.category,
    [filteredSlots, uploadSlot?.id],
  );
  const selectedUploadCategory = useMemo<DocumentCategory | null>(() => {
    if (activeCategoryFilters.length !== 1) return null;
    const [category] = activeCategoryFilters;
    if (!category || category === "uncategorized") return null;
    return category;
  }, [activeCategoryFilters]);
  const uploadDefaultCategory = uploadSlotCategory ?? selectedUploadCategory;
  const uploadDefaultTags = useMemo(() => {
    if (!uploadDefaultCategory) return defaultUploadTags;
    return Array.from(new Set([...defaultUploadTags, uploadDefaultCategory]));
  }, [defaultUploadTags, uploadDefaultCategory]);

  useEffect(() => {
    if (
      !documentFilterOptions.some(
        (option) => option.id === activeDocumentFilter,
      )
    ) {
      setActiveDocumentFilter("all");
    }
  }, [activeDocumentFilter, documentFilterOptions]);

  const statuses = useMemo(
    () =>
      deriveStatuses(
        displayDocuments,
        filteredSlots.map((s) => s.id),
      ),
    [displayDocuments, filteredSlots],
  );

  const { categorize } = useResultCategoryResolver();
  const slotClinicalValues = useMemo(
    () =>
      buildSlotTestResults(displayDocuments, displayTestResults, categorize),
    [categorize, displayDocuments, displayTestResults],
  );

  const slotDates = useMemo(
    () => buildSlotDates(displayDocuments),
    [displayDocuments],
  );

  const flatDocumentItems = useMemo<FlatDocumentItem[]>(() => {
    const items: FlatDocumentItem[] = [];

    for (const slot of filteredSlots) {
      const status = statuses[slot.id] ?? "not_started";
      if (status === "not_started") continue;
      const matchingDoc = displayDocuments?.find((doc) =>
        doc.tags.includes(slot.id),
      );
      const title = t(slot.nameKey);
      const subtitle = t(slot.whyKey);
      items.push({
        kind: "slot",
        id: slot.id,
        category: slot.category,
        slot,
        status,
        document: matchingDoc,
        date: slotDates[slot.id] ?? null,
        actionNeeded:
          status === "requested" ||
          (matchingDoc ? isDocumentRequested(matchingDoc) : false),
        searchText: `${title} ${subtitle}`.toLowerCase(),
        sortLabel: title,
      });
    }

    for (const doc of displayDocuments ?? []) {
      if (doc.tags.some((tag) => allSlotIds.has(tag))) continue;
      const category = getDocumentCategoryFromTags(doc.tags);
      const categoryMeta = getCategoryMeta(category);
      const categoryDescription = t(categoryMeta.descriptionKey);
      items.push({
        kind: "free",
        id: doc.id,
        category,
        document: doc,
        date: doc.documentDate ?? doc.createdAt ?? null,
        actionNeeded: isDocumentRequested(doc),
        searchText: `${doc.name} ${categoryDescription}`.toLowerCase(),
        sortLabel: doc.name,
      });
    }

    return items.sort(compareFlatDocumentItems);
  }, [allSlotIds, displayDocuments, filteredSlots, slotDates, statuses, t]);

  const hasUncategorizedDocuments = flatDocumentItems.some(
    (item) => item.category === "uncategorized",
  );

  const categoryFilterOptions = useMemo<CategoryFilterMeta[]>(() => {
    const options: CategoryFilterMeta[] = [...CATEGORIES];
    if (
      hasUncategorizedDocuments ||
      activeCategoryFilters.includes("uncategorized")
    ) {
      options.push(UNCATEGORIZED_CATEGORY);
    }
    return options;
  }, [activeCategoryFilters, hasUncategorizedDocuments]);

  const visibleDocumentItems = useMemo(
    () =>
      flatDocumentItems.filter((item) => {
        if (!flatDocumentItemMatchesFilter(item, activeDocumentFilter)) {
          return false;
        }
        if (
          activeCategoryFilters.length > 0 &&
          !activeCategoryFilters.includes(item.category)
        ) {
          return false;
        }
        if (searchQuery.trim()) {
          return item.searchText.includes(searchQuery.trim().toLowerCase());
        }
        return true;
      }),
    [
      activeCategoryFilters,
      activeDocumentFilter,
      flatDocumentItems,
      searchQuery,
    ],
  );

  const shouldGroupByCategory =
    activeDocumentFilter === "all" &&
    activeCategoryFilters.length === 0 &&
    !searchQuery.trim() &&
    visibleDocumentItems.length > 10;

  const groupedDocumentSections = useMemo(() => {
    if (!shouldGroupByCategory) return [];
    const buckets = new Map<DocumentFilterCategory, FlatDocumentItem[]>();
    for (const item of visibleDocumentItems) {
      const bucket = buckets.get(item.category);
      if (bucket) {
        bucket.push(item);
      } else {
        buckets.set(item.category, [item]);
      }
    }
    return GROUPED_CATEGORY_ORDER.map((category) => ({
      category,
      items: buckets.get(category) ?? [],
    })).filter((section) => section.items.length > 0);
  }, [shouldGroupByCategory, visibleDocumentItems]);

  const handleSlotPress = (id: string) => {
    const slot = filteredSlots.find((s) => s.id === id);
    if (!slot) return;

    const status = statuses[id] ?? "not_started";

    if (status === "have_it" && displayDocuments) {
      const matchingDoc = displayDocuments.find((doc) => doc.tags.includes(id));
      if (matchingDoc) {
        setPreviewDocumentInfo({
          slotId: slot.id,
          slotName: t(slot.nameKey),
          slotWhy: t(slot.whyKey),
          document: matchingDoc,
          clinicalValues: slotClinicalValues[slot.id],
          processingLookup: previewProcessingLookup(matchingDoc.id),
        });
        setPreviewModalVisible(true);
        return;
      }
    }

    setUploadSlot({
      id: slot.id,
      name: t(slot.nameKey),
      why: t(slot.whyKey),
    });
    setPendingUploadFiles([]);
    setUploadModalVisible(true);
  };

  const handleFreeUpload = () => {
    setPendingUploadFiles([]);
    setUploadSlot(null);
    setUploadModalVisible(true);
  };

  const handleDroppedFreeUpload = (files: SelectedFile[]) => {
    setPendingUploadFiles(files);
    setUploadSlot(null);
    setUploadModalVisible(true);
  };

  const handleDocumentFilterPress = (filterId: string) => {
    if (filterId === "all") {
      setActiveDocumentFilter("all");
      setActiveCategoryFilters([]);
      return;
    }
    setActiveDocumentFilter(filterId);
  };

  const handleCategoryFilterPress = (category: DocumentFilterCategory) => {
    setActiveCategoryFilters((prev) =>
      prev.includes(category)
        ? prev.filter((item) => item !== category)
        : [...prev, category],
    );
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setActiveDocumentFilter("all");
    setActiveCategoryFilters([]);
  };

  const handleUploadClose = () => {
    setUploadModalVisible(false);
    setUploadSlot(null);
    setPendingUploadFiles([]);
  };

  const handleReuploadFromPreview = () => {
    setPreviewModalVisible(false);
    if (previewDocumentInfo) {
      setUploadSlot({
        id: previewDocumentInfo.slotId,
        name: previewDocumentInfo.slotName,
        why: previewDocumentInfo.slotWhy,
      });
      setPendingUploadFiles([]);
      setUploadModalVisible(true);
    }
  };

  const renderDocumentItem = (item: FlatDocumentItem, index: number) => {
    const categoryMeta = getCategoryMeta(item.category);
    // Stagger caps at ~400ms so a long list doesn't make the tail feel laggy.
    const entering = reducedMotion
      ? undefined
      : FadeIn.delay(Math.min(index * 40, 400)).duration(220);
    const layout = reducedMotion ? undefined : LinearTransition.springify();

    if (item.kind === "slot") {
      const primaryClinicalValue =
        slotClinicalValues[item.slot.id]?.[0] ?? null;
      if (item.category === "medical_records" && primaryClinicalValue) {
        const insight = clinicalValueToInsight(
          item.slot,
          primaryClinicalValue,
          t,
        );
        return (
          <ReAnimated.View key={item.id} entering={entering} layout={layout}>
            <DocumentTestResultRow
              title={insight.testName}
              subtitle={insight.testPurpose}
              dateLabel={formatDocumentRowDate(item.date, t, locale)}
              value={insight.value}
              unit={insight.unit}
              category={insight.category}
              onPress={() => handleSlotPress(item.slot.id)}
            />
          </ReAnimated.View>
        );
      }

      return (
        <ReAnimated.View key={item.id} entering={entering} layout={layout}>
          <DocumentRow
            title={t(item.slot.nameKey)}
            subtitle={t(item.slot.whyKey)}
            dateLabel={formatDocumentRowDate(item.date, t, locale)}
            imageSource={categoryMeta.image}
            status={getDocumentRowStatus(item.status)}
            onPress={() => handleSlotPress(item.slot.id)}
          />
        </ReAnimated.View>
      );
    }

    const openDocument = () => {
      setPreviewDocumentInfo({
        slotId: item.document.id,
        slotName: item.document.name,
        slotWhy: "",
        document: item.document,
        processingLookup: previewProcessingLookup(item.document.id),
      });
      setPreviewModalVisible(true);
    };

    return (
      <ReAnimated.View key={item.id} entering={entering} layout={layout}>
        <DocumentRow
          title={item.document.name}
          subtitle={t(categoryMeta.descriptionKey)}
          dateLabel={formatDocumentRowDate(item.date, t, locale)}
          imageSource={categoryMeta.image}
          icon={
            categoryMeta.image ? undefined : (
              <FileText size={17} color={EMBER.colors.warm} strokeWidth={1.6} />
            )
          }
          status={item.actionNeeded ? "requested" : "on-file"}
          onPress={openDocument}
        />
      </ReAnimated.View>
    );
  };

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View className="flex-row items-start justify-between">
          <BackLink
            label={
              route.params?.returnTab
                ? t("you.shared.back")
                : t("documents.back")
            }
            action="documents.back"
            onPress={handleBack}
            style={{ marginBottom: 8 }}
          />
        </View>

        <View style={{ marginBottom: 24 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 38,
              fontWeight: "700",
              color: "#3B1A08",
              lineHeight: 42,
              letterSpacing: -0.5,
              ...(Platform.OS === "web" && {
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E"), linear-gradient(135deg, #3B1A08 0%, #7A3E15 35%, #3B1A08 60%, #6B3410 85%, #3B1A08 100%)`,
                backgroundSize: "200px 200px, 100% 100%",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }),
            }}
          >
            {t("documents.title")}
          </Text>
        </View>
        {documentsDataState === "unavailable" ? (
          <ClinicalDataStatus
            state="unavailable"
            title={t("clinical_data.unavailable_title")}
            message={t("clinical_data.unavailable_body")}
            retryLabel={t("clinical_data.retry")}
            retryingLabel={t("clinical_data.retrying")}
            onRetry={retryDocuments}
            isRetrying={isRetryingDocuments}
            testID="documents-unavailable"
          />
        ) : (
          <>
            {documentsDataState === "stale" ? (
              <ClinicalDataStatus
                state="stale"
                message={t("clinical_data.stale_body")}
                retryLabel={t("clinical_data.retry")}
                retryingLabel={t("clinical_data.retrying")}
                onRetry={retryDocuments}
                isRetrying={isRetryingDocuments}
                style={{ marginBottom: 12 }}
                testID="documents-stale"
              />
            ) : null}

            {/* Search bar + filter */}
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
                marginBottom: 12,
              }}
            >
              <MoonSearchInput
                placeholder={t("documents.search_placeholder")}
                value={searchQuery}
                onChangeText={setSearchQuery}
                onClear={() => setSearchQuery("")}
                tone="glass"
                style={{ flex: 1, borderRadius: 12 }}
              />
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t("documents.upload.upload_a_document")}
                onPress={() => {
                  capturePress(
                    pressLabel("upload_document"),
                    "upload_document",
                  );
                  handleFreeUpload();
                }}
                style={{
                  ...cardSurface(),
                  width: 50,
                  height: 44,
                  borderRadius: 12,
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                }}
              >
                <Animated.Image
                  source={require("@src/apps/moon/assets/images/care-hero-glow-gradient.png")}
                  style={{
                    position: "absolute",
                    left: -7,
                    top: -8,
                    width: 64,
                    height: 60,
                    opacity: 1,
                    transform: [
                      {
                        translateX: uploadButtonDrift.interpolate({
                          inputRange: [0, 1],
                          outputRange: [-5, 6],
                        }),
                      },
                      {
                        translateY: uploadButtonDrift.interpolate({
                          inputRange: [0, 1],
                          outputRange: [3, -4],
                        }),
                      },
                      {
                        scale: uploadButtonDrift.interpolate({
                          inputRange: [0, 1],
                          outputRange: [1.02, 1.08],
                        }),
                      },
                    ],
                  }}
                  resizeMode="cover"
                />
                <View style={{ zIndex: 2, elevation: 2 }}>
                  <Plus size={19} color="#fff" strokeWidth={2.25} />
                </View>
              </Pressable>
            </View>

            {!displayDocumentsLoading && flatDocumentItems.length > 0 ? (
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                style={{ marginHorizontal: -16, marginBottom: 18 }}
                contentContainerStyle={{
                  gap: 10,
                  paddingLeft: 16,
                  paddingRight: 24,
                }}
              >
                {primaryDocumentFilterOptions.map((filter) => {
                  const selected =
                    filter.id === "all"
                      ? activeDocumentFilter === "all" &&
                        activeCategoryFilters.length === 0
                      : activeDocumentFilter === filter.id;
                  return (
                    <MoonFilterChip
                      key={filter.id}
                      label={filter.label}
                      icon={filter.icon}
                      selected={selected}
                      onPress={() => handleDocumentFilterPress(filter.id)}
                      tone="glass"
                      showCheck={false}
                    />
                  );
                })}
                {categoryFilterOptions.map((category) => (
                  <MoonFilterChip
                    key={category.key}
                    label={t(category.labelKey)}
                    icon={category.icon}
                    selected={activeCategoryFilters.includes(category.key)}
                    onPress={() => handleCategoryFilterPress(category.key)}
                    tone="glass"
                  />
                ))}
                {contextualDocumentFilterOptions.map((filter) => (
                  <MoonFilterChip
                    key={filter.id}
                    label={filter.label}
                    icon={filter.icon}
                    selected={activeDocumentFilter === filter.id}
                    onPress={() => handleDocumentFilterPress(filter.id)}
                    tone="glass"
                    showCheck={false}
                  />
                ))}
              </ScrollView>
            ) : null}

            {displayDocumentsLoading ? (
              <View>
                {Array.from({ length: 4 }).map((_, i) => (
                  <ListRowSkeleton
                    key={i}
                    showLeadingSquare
                    showSubtitle
                    showTrailing
                  />
                ))}
              </View>
            ) : null}
            {!displayDocumentsLoading && flatDocumentItems.length === 0 ? (
              <DocumentsEmptyState
                variant="empty"
                onUpload={handleFreeUpload}
                onFilesSelected={handleDroppedFreeUpload}
                onClearFilters={handleClearFilters}
              />
            ) : null}
            {!displayDocumentsLoading &&
            flatDocumentItems.length > 0 &&
            visibleDocumentItems.length === 0 ? (
              <DocumentsEmptyState
                variant="filtered"
                onUpload={handleFreeUpload}
                onFilesSelected={handleDroppedFreeUpload}
                onClearFilters={handleClearFilters}
              />
            ) : null}
            {!displayDocumentsLoading && visibleDocumentItems.length > 0 ? (
              shouldGroupByCategory ? (
                <View style={{ gap: 20 }}>
                  {groupedDocumentSections.map((section) => (
                    <View key={section.category} style={{ gap: 10 }}>
                      <SectionTitle>
                        {t(getCategoryMeta(section.category).labelKey)}
                      </SectionTitle>
                      {section.items.map(renderDocumentItem)}
                    </View>
                  ))}
                </View>
              ) : (
                <View style={{ gap: 10 }}>
                  {visibleDocumentItems.map(renderDocumentItem)}
                </View>
              )
            ) : null}
          </>
        )}
      </ScrollView>

      {documentUploadPrototypeState ? (
        <DocumentUploadInteractionPrototype
          state={documentUploadPrototypeState}
          visible
          onClose={() => navigation.goBack()}
        />
      ) : (
        <DocumentUploadModal
          allowAnyFile
          visible={uploadModalVisible}
          onClose={handleUploadClose}
          slot={uploadSlot}
          defaultCategory={uploadDefaultCategory}
          defaultTags={uploadDefaultTags}
          initialFiles={pendingUploadFiles}
          autoPickOnOpen={!visualEvidenceRoute}
        />
      )}

      <DocumentPreviewModal
        visible={previewModalVisible}
        onClose={() => setPreviewModalVisible(false)}
        documentInfo={previewDocumentInfo}
        onReupload={handleReuploadFromPreview}
      />
    </GlassScreenLayout>
  );
}
